import React, { useState, useEffect, useRef } from 'react';
import { FilterParams } from '../../types';

interface SearchFiltersProps {
  onFiltersChange: (filters: FilterParams) => void;
  initialFilters?: FilterParams;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({ onFiltersChange, initialFilters = {} }) => {
  const [filters, setFilters] = useState<FilterParams>(initialFilters);
  const [isExpanded, setIsExpanded] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  // 星数范围选项
  const starRanges = [
    { label: '不限', min: undefined, max: undefined },
    { label: '1+ 星', min: 1, max: undefined },
    { label: '10+ 星', min: 10, max: undefined },
    { label: '50+ 星', min: 50, max: undefined },
    { label: '100+ 星', min: 100, max: undefined },
    { label: '500+ 星', min: 500, max: undefined },
    { label: '1000+ 星', min: 1000, max: undefined },
    { label: '5000+ 星', min: 5000, max: undefined },
    { label: '10000+ 星', min: 10000, max: undefined }
  ];

  // Fork数范围选项
  const forkRanges = [
    { label: '不限', min: undefined },
    { label: '1+ Fork', min: 1 },
    { label: '5+ Fork', min: 5 },
    { label: '10+ Fork', min: 10 },
    { label: '50+ Fork', min: 50 },
    { label: '100+ Fork', min: 100 },
    { label: '500+ Fork', min: 500 }
  ];

  // 仓库大小选项
  const sizeOptions = [
    { value: 'all', label: '不限大小' },
    { value: 'small', label: '小型 (<1MB)' },
    { value: 'medium', label: '中型 (1-10MB)' },
    { value: 'large', label: '大型 (>10MB)' }
  ];





  // 处理筛选条件变化
  const handleFilterChange = (key: keyof FilterParams, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // 处理星数范围选择
  const handleStarRangeChange = (min?: number, max?: number) => {
    const newFilters = { 
      ...filters, 
      min_stars_filter: min,
      max_stars_filter: max 
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // 重置所有筛选条件
  const resetFilters = () => {
    const emptyFilters: FilterParams = {};
    setFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  // 检查是否有活跃的筛选条件
  const hasActiveFilters = Object.values(filters).some(value =>
    value !== undefined && value !== '' && value !== 'all'
  );

  // 点击外部关闭筛选面板
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <div ref={filtersRef} className={`search-filters ${isExpanded ? 'expanded' : ''}`}>
      <div
        className="filters-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="filters-title">
          <i className="fas fa-filter"></i>
          <span>筛选</span>
          {hasActiveFilters && <span className="active-indicator">●</span>}
        </div>
        <div className="filters-actions">
          {hasActiveFilters && (
            <button 
              type="button" 
              className="btn-reset-filters"
              onClick={resetFilters}
              title="重置筛选条件"
            >
              <i className="fas fa-undo"></i>
              重置
            </button>
          )}
          <button
            type="button"
            className="btn-toggle-filters"
            onClick={(e) => {
              e.stopPropagation();
              console.log('筛选按钮被点击，当前状态:', isExpanded);
              setIsExpanded(!isExpanded);
            }}
            title={isExpanded ? '收起筛选' : '展开筛选'}
          >
            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="filters-content">
          <div className="filters-grid">
            {/* 描述语言筛选 */}
            <div className="filter-group">
              <label className="filter-label">
                <i className="fas fa-language"></i>
                项目描述语言
              </label>
              <select 
                className="form-select filter-select"
                value={filters.description_language || 'all'}
                onChange={(e) => handleFilterChange('description_language', e.target.value === 'all' ? undefined : e.target.value)}
              >
                <option value="all">不限</option>
                <option value="chinese">中文</option>
                <option value="english">英文</option>
              </select>
            </div>

            {/* 星数筛选 */}
            <div className="filter-group">
              <label className="filter-label">
                <i className="fas fa-star"></i>
                项目星数
              </label>
              <select 
                className="form-select filter-select"
                value={filters.min_stars_filter || ''}
                onChange={(e) => {
                  const selectedRange = starRanges.find(range => range.min?.toString() === e.target.value);
                  if (selectedRange) {
                    handleStarRangeChange(selectedRange.min, selectedRange.max);
                  } else {
                    handleStarRangeChange(undefined, undefined);
                  }
                }}
              >
                {starRanges.map((range, index) => (
                  <option key={index} value={range.min || ''}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Fork数筛选 */}
            <div className="filter-group">
              <label className="filter-label">
                <i className="fas fa-code-branch"></i>
                Fork数量
              </label>
              <select 
                className="form-select filter-select"
                value={filters.min_forks_filter || ''}
                onChange={(e) => handleFilterChange('min_forks_filter', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                {forkRanges.map((range, index) => (
                  <option key={index} value={range.min || ''}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>


            {/* 仓库大小 */}
            <div className="filter-group">
              <label className="filter-label">
                <i className="fas fa-database"></i>
                仓库大小
              </label>
              <select 
                className="form-select filter-select"
                value={filters.size_range || 'all'}
                onChange={(e) => handleFilterChange('size_range', e.target.value === 'all' ? undefined : e.target.value)}
              >
                {sizeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>


          </div>

          {/* 布尔筛选条件 */}
          <div className="boolean-filters">
            <div className="boolean-filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.has_topics || false}
                  onChange={(e) => handleFilterChange('has_topics', e.target.checked || undefined)}
                />
                <span className="checkmark"></span>
                <i className="fas fa-tags"></i>
                有主题标签
              </label>
            </div>

            <div className="boolean-filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.has_wiki || false}
                  onChange={(e) => handleFilterChange('has_wiki', e.target.checked || undefined)}
                />
                <span className="checkmark"></span>
                <i className="fas fa-book"></i>
                有Wiki文档
              </label>
            </div>

            <div className="boolean-filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.has_pages || false}
                  onChange={(e) => handleFilterChange('has_pages', e.target.checked || undefined)}
                />
                <span className="checkmark"></span>
                <i className="fas fa-globe"></i>
                有GitHub Pages
              </label>
            </div>

            <div className="boolean-filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.is_archived === false}
                  onChange={(e) => handleFilterChange('is_archived', e.target.checked ? false : undefined)}
                />
                <span className="checkmark"></span>
                <i className="fas fa-check-circle"></i>
                排除已归档
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
